import { Button } from "@/components/ui/button"
import { Form, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { toast } from "@/components/ui/use-toast"
import { safeExec, useAsyncEffect } from "@/utils"
import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { api_fetch } from "../api/client.tsx"
import { Row } from "./index.tsx"
import { CheckCircleIcon, ClockIcon } from "@heroicons/react/24/outline"
import { format, parseISO } from "date-fns"

interface Invitation {
  id: string
  email: string
  created_at: string
  expires_at: string
  accepted_at: string | null
}

const inviteSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
})

type InviteFormData = z.infer<typeof inviteSchema>

export function TeamInvitationsSettings() {
  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [loading, setLoading] = useState(false)

  const form = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      email: "",
    },
  })

  async function loadInvitations() {
    const res = await api_fetch<Invitation[]>("/invitations")
    if (res.ok) {
      setInvitations(res.value.data)
    }
  }

  useAsyncEffect(() => loadInvitations(), [])

  const onSubmit = async (data: InviteFormData) => {
    setLoading(true)
    safeExec(async () => {
      const res = await api_fetch<Invitation>("/invitations", {
        method: "POST",
        body: data,
      })

      if (res.ok) {
        toast({
          title: "Invitation Sent",
          description: `An invitation has been sent to ${data.email}`,
        })
        form.reset()
        await loadInvitations()
      } else {
        toast({
          title: "Failed to Send Invitation",
          description: res.error?.message || "An error occurred",
          variant: "destructive",
        })
      }
      setLoading(false)
    })
  }

  function getInvitationStatus(invitation: Invitation) {
    if (invitation.accepted_at) {
      return {
        text: `Accepted ${format(parseISO(invitation.accepted_at), "MMM d, yyyy")}`,
        icon: <CheckCircleIcon class="h-5 w-5 text-green-500" />,
        color: "text-green-600",
      }
    }

    const now = new Date()
    const expiresAt = parseISO(invitation.expires_at)

    if (now > expiresAt) {
      return {
        text: "Expired",
        icon: <ClockIcon class="h-5 w-5 text-gray-400" />,
        color: "text-gray-500",
      }
    }

    return {
      text: `Pending (expires ${format(expiresAt, "MMM d")})`,
      icon: <ClockIcon class="h-5 w-5 text-blue-500" />,
      color: "text-blue-600",
    }
  }

  return (
    <div class="space-y-6">
      <div>
        <h3 class="text-sm font-medium text-gray-900 mb-3">Invite Team Member</h3>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} class="flex gap-3">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem class="flex-1">
                  <Input {...field} type="email" placeholder="colleague@company.com" class="w-full" />
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={loading}>
              {loading ? "Sending..." : "Send Invitation"}
            </Button>
          </form>
        </Form>
      </div>

      {invitations.length > 0 && (
        <div>
          <h3 class="text-sm font-medium text-gray-900 mb-3">Invitations</h3>
          <div class="space-y-2">
            {invitations.map(invitation => {
              const status = getInvitationStatus(invitation)
              return (
                <div key={invitation.id} class="flex items-center justify-between py-2 border-b border-gray-100">
                  <div class="flex items-center gap-3">
                    {status.icon}
                    <div>
                      <div class="text-sm font-medium text-gray-900">{invitation.email}</div>
                      <div class={`text-xs ${status.color}`}>{status.text}</div>
                    </div>
                  </div>
                  <div class="text-xs text-gray-500">Sent {format(parseISO(invitation.created_at), "MMM d, yyyy")}</div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
